import { useState, useEffect } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '@/lib/supabaseClient';
import { toast } from 'sonner';
import {
  Thermometer, AlertTriangle, TrendingUp,
  Building2, Loader2, ChevronDown, Leaf, Factory, Droplets,
  Save, History, Trash2, Calendar
} from 'lucide-react';

// Industry-specific stress test scenarios
const SCENARIO_DATA = {
  "1.5": {
    name: "1.5°C Pathway",
    description: "Paris Agreement aligned scenario - Aggressive decarbonization",
    color: "green",
    physical_risks: "moderate",
    transition_risks: "high",
    industries: {
      "agriculture": {
        summary: "Under a 1.5°C scenario, agricultural operations in Australia face moderate physical risks from changing rainfall patterns and increased heat stress events. However, transition risks are significant as carbon pricing mechanisms and mandatory emissions reporting requirements intensify. Early adopters of regenerative practices and precision agriculture technologies are likely to gain competitive advantages.",
        opportunities: ["Carbon farming credits", "Premium sustainable produce markets", "Agri-tech adoption incentives"],
        risks: ["Rising input costs from carbon pricing", "Value chain disclosure requirements", "Water allocation restrictions"]
      },
      "construction": {
        summary: "The construction sector faces high transition risks under 1.5°C as building codes rapidly evolve to mandate net-zero standards. Physical risks remain moderate but concentrated in coastal developments. Companies investing in sustainable materials and energy-efficient construction methods will be better positioned for government contracts requiring green credentials.",
        opportunities: ["Green building certifications", "Retrofit market expansion", "Sustainable materials innovation"],
        risks: ["Stranded asset risk for non-compliant buildings", "Material cost volatility", "Skilled labour shortages in green construction"]
      },
      "manufacturing": {
        summary: "Manufacturing operations face substantial transition risks as energy costs increase and emissions reporting becomes mandatory. Physical risks are moderate but value chain disruptions from climate events could impact production. Electrification of processes and circular economy adoption present significant opportunities.",
        opportunities: ["Clean technology exports", "Circular economy revenue streams", "Energy efficiency gains"],
        risks: ["Carbon border adjustments", "Energy price volatility", "Scope 3 value chain requirements"]
      },
      "retail": {
        summary: "Retail businesses face moderate physical risks from extreme weather disrupting logistics, with high transition risks from changing consumer preferences and value chain transparency requirements. Companies with strong ESG credentials and sustainable product lines will capture growing market share.",
        opportunities: ["Sustainable product premiums", "ESG-conscious consumer growth", "Value chain efficiency"],
        risks: ["Consumer spending shifts", "Greenwashing litigation risk", "Packaging regulation compliance"]
      },
      "transport": {
        summary: "The transport sector faces the highest transition risks under 1.5°C as fleet electrification becomes essential and fuel costs incorporate full carbon pricing. Physical risks from extreme weather events will disrupt operations. Early investment in EV infrastructure provides significant first-mover advantages.",
        opportunities: ["EV fleet transition incentives", "Last-mile delivery innovation", "Carbon-neutral logistics premium"],
        risks: ["Stranded fossil fuel assets", "Infrastructure investment requirements", "Driver retraining costs"]
      },
      "professional_services": {
        summary: "Professional services face low physical risks but moderate transition risks as clients demand ESG expertise and carbon-neutral operations. Firms building climate advisory capabilities will access growing markets, while those slow to adapt may lose clients to competitors with stronger sustainability credentials.",
        opportunities: ["Climate advisory services", "ESG reporting expertise", "Sustainable finance advisory"],
        risks: ["Client ESG requirements", "Talent competition for sustainability experts", "Office decarbonization costs"]
      },
      "default": {
        summary: "Under a 1.5°C pathway, your organisation will face moderate physical climate risks but significant transition risks as Australia accelerates decarbonization policies. Carbon pricing, mandatory climate disclosure, and shifting consumer preferences will reshape competitive dynamics. Early action on emissions reduction provides strategic advantages.",
        opportunities: ["First-mover advantages in decarbonization", "Access to green finance", "Enhanced brand reputation"],
        risks: ["Carbon pricing exposure", "Regulatory compliance costs", "Value chain disruptions"]
      }
    }
  },
  "2.5": {
    name: "2.5°C Pathway",
    description: "Current policies scenario - Moderate mitigation efforts",
    color: "amber",
    physical_risks: "high",
    transition_risks: "moderate",
    industries: {
      "agriculture": {
        summary: "A 2.5°C scenario presents severe physical risks for Australian agriculture including prolonged droughts, increased bushfire frequency, and heat stress on livestock and crops. While transition risks are lower due to slower policy action, physical damages and insurance costs will significantly impact profitability. Adaptation investments become critical.",
        opportunities: ["Climate-resilient crop varieties", "Water efficiency technology", "Insurance product innovation"],
        risks: ["Crop yield reductions up to 25%", "Water scarcity and allocation conflicts", "Increased pest and disease pressure"]
      },
      "construction": {
        summary: "Construction faces high physical risks under 2.5°C with increased extreme weather events damaging sites and infrastructure. Coastal and flood-prone developments face particular exposure. While regulatory transition is slower, physical climate impacts on materials, labour productivity, and project timelines present operational challenges.",
        opportunities: ["Climate-resilient infrastructure demand", "Disaster recovery construction", "Coastal protection works"],
        risks: ["Project delays from extreme weather", "Material degradation from heat", "Insurance and liability costs"]
      },
      "manufacturing": {
        summary: "Manufacturing operations face significant physical risks from heat stress affecting worker productivity and equipment efficiency. Value chain disruptions from global climate impacts become more frequent. While domestic transition policies are less aggressive, export markets may impose carbon border adjustments.",
        opportunities: ["Adaptation technology manufacturing", "Domestic value chain reshoring", "Heat-resilient facility design"],
        risks: ["Production losses from heat events", "Value chain volatility", "Export market carbon tariffs"]
      },
      "retail": {
        summary: "Retail faces high physical risks from extreme weather disrupting stores, logistics, and customer foot traffic. Consumer spending patterns become more volatile with increased cost-of-living pressures from climate damages. Value chain reliability becomes a key competitive differentiator.",
        opportunities: ["Climate adaptation products", "E-commerce resilience", "Local sourcing advantages"],
        risks: ["Store damage and closures", "Value chain disruptions", "Consumer spending volatility"]
      },
      "transport": {
        summary: "Transport infrastructure faces severe physical risks under 2.5°C including road damage from heat, flooding of depots, and extreme weather delays. While EV transition pressures are lower, physical operational disruptions and insurance costs present ongoing challenges. Climate-resilient route planning becomes essential.",
        opportunities: ["Climate-resilient logistics services", "Emergency response transport", "Route optimization technology"],
        risks: ["Infrastructure damage costs", "Fleet downtime from extreme weather", "Insurance premium increases"]
      },
      "professional_services": {
        summary: "Professional services face increased physical risks to office infrastructure and business continuity from extreme weather events. Client demand shifts toward climate adaptation services rather than mitigation. Firms with disaster recovery capabilities and remote work infrastructure will maintain operations during disruptions.",
        opportunities: ["Adaptation advisory services", "Business continuity consulting", "Remote work solutions"],
        risks: ["Office disruptions from extreme weather", "Client financial stress", "Talent attraction challenges in affected regions"]
      },
      "default": {
        summary: "Under a 2.5°C scenario, your organisation faces high physical climate risks including extreme heat, bushfires, flooding, and value chain disruptions. While transition risks are lower due to delayed policy action, the cumulative costs of physical climate damages will significantly impact operations and profitability. Adaptation and resilience investments become business-critical.",
        opportunities: ["Adaptation product and service markets", "Climate resilience consulting", "Business continuity advantages"],
        risks: ["Physical asset damage", "Operational disruptions", "Insurance cost increases"]
      }
    }
  }
};

const STATE_CONTEXT = {
  "NSW": "New South Wales faces increasing bushfire risk, coastal erosion, and urban heat island effects in Sydney.",
  "VIC": "Victoria experiences compound climate risks including bushfires, floods, and extended drought periods.",
  "QLD": "Queensland faces cyclone intensity increases, coral bleaching impacts on tourism, and extreme heat events.",
  "WA": "Western Australia sees declining rainfall in the southwest and increased bushfire risk across agricultural zones.",
  "SA": "South Australia faces severe heatwaves, water scarcity, and Murray-Darling system stress.",
  "TAS": "Tasmania experiences changing precipitation patterns and increasing bushfire risk in previously low-risk areas.",
  "NT": "Northern Territory faces extreme heat increases, cyclone intensification, and Indigenous community impacts.",
  "ACT": "Australian Capital Territory sees smoke exposure risk from regional bushfires and urban heat stress."
};

const ScenarioStressTestPage = () => {
  const navigate = useNavigate();
  const { currentOrg, user, userData } = useOutletContext();
  const currentUserId = userData?.id;
  const [loading, setLoading] = useState(true);
  const [selectedScenario, setSelectedScenario] = useState("1.5");
  const [showScenarioDropdown, setShowScenarioDropdown] = useState(false);
  const [savedAnalyses, setSavedAnalyses] = useState([]);
  const [showSavedAnalyses, setShowSavedAnalyses] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (currentOrg) {
      setLoading(false);
    }
  }, [currentOrg]);

  // Fetch saved analyses when org loads
  useEffect(() => {
    if (currentOrg?.id) {
      fetchSavedAnalyses();
    }
  }, [currentOrg?.id]);

  const fetchSavedAnalyses = async () => {
    try {
      const { data, error } = await supabase
        .from('scenario_analysis')
        .select('*')
        .eq('organisation_id', currentOrg.id)
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) throw error;
      setSavedAnalyses(data || []);
    } catch (err) {
    }
  };

  const saveAnalysis = async () => {
    if (!currentOrg?.id) {
      toast.error('No organisation selected');
      return;
    }

    setSaving(true);
    try {
      const scenario = SCENARIO_DATA[selectedScenario];
      const industryKey = getIndustryKey(currentOrg?.industry);
      const industryData = scenario.industries[industryKey] || scenario.industries['default'];

      const { data, error } = await supabase
        .from('scenario_analysis')
        .insert({
          organisation_id: currentOrg.id,
          created_by: currentUserId,
          scenario_type: selectedScenario,
          scenario_name: scenario.name,
          industry: currentOrg.industry || industryKey,
          physical_risk_level: scenario.physical_risks,
          transition_risk_level: scenario.transition_risks,
          summary: industryData.summary,
          opportunities: industryData.opportunities,
          risks: industryData.risks,
          notes: `Analysis based on ${currentOrg.industry || 'general'} industry profile`
        })
        .select()
        .single();

      if (error) throw error;

      toast.success('Scenario analysis saved successfully');
      fetchSavedAnalyses();
    } catch (err) {
      console.error('Save error:', err);
      toast.error('Failed to save analysis: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteAnalysis = async (id) => {
    try {
      const { error } = await supabase
        .from('scenario_analysis')
        .delete()
        .eq('id', id);

      if (error) throw error;
      toast.success('Analysis deleted');
      fetchSavedAnalyses();
    } catch (err) {
      toast.error('Failed to delete: ' + err.message);
    }
  };

  const loadAnalysis = (analysis) => {
    setSelectedScenario(analysis.scenario_type);
    setShowSavedAnalyses(false);
    toast.info(`Loaded ${analysis.scenario_name} analysis from ${new Date(analysis.created_at).toLocaleDateString()}`);
  };


  const getIndustryKey = (industry) => {
    if (!industry) return 'default';
    const lower = industry.toLowerCase();
    if (lower.includes('agricult') || lower.includes('farm')) return 'agriculture';
    if (lower.includes('construct') || lower.includes('build')) return 'construction';
    if (lower.includes('manufactur')) return 'manufacturing';
    if (lower.includes('retail') || lower.includes('shop')) return 'retail';
    if (lower.includes('transport') || lower.includes('logist')) return 'transport';
    if (lower.includes('professional') || lower.includes('consult') || lower.includes('service')) return 'professional_services';
    return 'default';
  };

  const scenario = SCENARIO_DATA[selectedScenario];
  const industryKey = getIndustryKey(currentOrg?.industry);
  const industryData = scenario.industries[industryKey] || scenario.industries['default'];
  const stateContext = STATE_CONTEXT[currentOrg?.state] || '';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA]">
        <Loader2 className="w-8 h-8 animate-spin text-[#003B5C]" />
      </div>
    );
  }

  return (
    <div className="bg-[#F8F9FA] min-h-[calc(100vh-56px)]" data-testid="stress-test-page">
      {/* Header */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#003B5C] flex items-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <Thermometer className="w-7 h-7 mr-3 text-[#74A57F]" />
              Scenario Stress Test
            </h1>
            <p className="text-slate-600 mt-1">
              {currentOrg?.name} • Assess climate resilience under different warming scenarios
            </p>
          </div>
          <div className="flex items-center space-x-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowSavedAnalyses(!showSavedAnalyses)}
              className="border-slate-300"
              data-testid="view-saved-analyses-btn"
            >
              <History className="w-4 h-4 mr-2" />
              Saved ({savedAnalyses.length})
            </Button>
            <Button
              size="sm"
              onClick={saveAnalysis}
              disabled={saving}
              className="bg-[#74A57F] hover:bg-[#5d8a66] text-white"
              data-testid="save-analysis-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Analysis
            </Button>
          </div>
        </div>
      </div>

      {/* Saved Analyses Panel */}
      {showSavedAnalyses && savedAnalyses.length > 0 && (
        <div className="max-w-7xl mx-auto px-6 pt-4">
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <h3 className="font-medium text-slate-700 mb-3 flex items-center">
              <History className="w-4 h-4 mr-2" />
              Saved Analyses
            </h3>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {savedAnalyses.map((analysis) => (
                <div
                  key={analysis.id}
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                >
                  <button
                    onClick={() => loadAnalysis(analysis)}
                    className="flex-1 text-left"
                  >
                    <div className="flex items-center space-x-3">
                      <span className={`w-3 h-3 rounded-full ${analysis.scenario_type === '1.5' ? 'bg-green-500' :
                        analysis.scenario_type === '2.5' ? 'bg-amber-500' : 'bg-red-500'
                        }`} />
                      <div>
                        <div className="font-medium text-sm text-slate-700">{analysis.scenario_name}</div>
                        <div className="text-xs text-slate-500 flex items-center mt-0.5">
                          <Calendar className="w-3 h-3 mr-1" />
                          {new Date(analysis.created_at).toLocaleDateString('en-AU', {
                            day: 'numeric', month: 'short', year: 'numeric'
                          })}
                          {analysis.industry && <span className="ml-2">• {analysis.industry}</span>}
                        </div>
                      </div>
                    </div>
                  </button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => deleteAnalysis(analysis.id)}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Scenario Toggle */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Thermometer className="w-5 h-5 text-[#003B5C]" />
              <span className="font-medium text-slate-700">Select Warming Scenario</span>
            </div>

            <div className="relative">
              <button
                onClick={() => setShowScenarioDropdown(!showScenarioDropdown)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg border-2 transition-colors ${selectedScenario === "1.5"
                  ? 'border-green-500 bg-green-50 text-green-700'
                  : 'border-amber-500 bg-amber-50 text-amber-700'
                  }`}
                data-testid="scenario-toggle"
              >
                <span className="font-semibold">{scenario.name}</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${showScenarioDropdown ? 'rotate-180' : ''}`} />
              </button>

              {showScenarioDropdown && (
                <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-lg border border-slate-200 z-50">
                  <button
                    onClick={() => { setSelectedScenario("1.5"); setShowScenarioDropdown(false); }}
                    className={`w-full px-4 py-3 text-left hover:bg-slate-50 flex items-center justify-between ${selectedScenario === "1.5" ? 'bg-green-50' : ''}`}
                    data-testid="scenario-1.5-option"
                  >
                    <div>
                      <div className="font-medium text-slate-800">1.5°C Pathway</div>
                      <div className="text-xs text-slate-500">Paris Agreement aligned</div>
                    </div>
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">Low warming</span>
                  </button>
                  <button
                    onClick={() => { setSelectedScenario("2.5"); setShowScenarioDropdown(false); }}
                    className={`w-full px-4 py-3 text-left hover:bg-slate-50 flex items-center justify-between ${selectedScenario === "2.5" ? 'bg-amber-50' : ''}`}
                    data-testid="scenario-2.5-option"
                  >
                    <div>
                      <div className="font-medium text-slate-800">2.5°C Pathway</div>
                      <div className="text-xs text-slate-500">Current policies</div>
                    </div>
                    <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded">High warming</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 pb-8 space-y-6">
        {/* Risk Summary Cards */}
        <div className="grid md:grid-cols-2 gap-4">
          <div className={`rounded-xl p-5 border-2 ${scenario.physical_risks === 'high'
            ? 'bg-red-50 border-red-200'
            : 'bg-amber-50 border-amber-200'
            }`} data-testid="physical-risks-card">
            <div className="flex items-center space-x-2 mb-2">
              <AlertTriangle className={`w-5 h-5 ${scenario.physical_risks === 'high' ? 'text-red-500' : 'text-amber-500'}`} />
              <span className="font-semibold text-slate-800">Physical Risks</span>
            </div>
            <div className={`text-2xl font-bold capitalize ${scenario.physical_risks === 'high' ? 'text-red-600' : 'text-amber-600'
              }`}>
              {scenario.physical_risks}
            </div>
            <p className="text-sm text-slate-600 mt-1">
              Extreme weather, sea level rise, temperature impacts
            </p>
          </div>

          <div className={`rounded-xl p-5 border-2 ${scenario.transition_risks === 'high'
            ? 'bg-purple-50 border-purple-200'
            : 'bg-blue-50 border-blue-200'
            }`} data-testid="transition-risks-card">
            <div className="flex items-center space-x-2 mb-2">
              <TrendingUp className={`w-5 h-5 ${scenario.transition_risks === 'high' ? 'text-purple-500' : 'text-blue-500'}`} />
              <span className="font-semibold text-slate-800">Transition Risks</span>
            </div>
            <div className={`text-2xl font-bold capitalize ${scenario.transition_risks === 'high' ? 'text-purple-600' : 'text-blue-600'
              }`}>
              {scenario.transition_risks}
            </div>
            <p className="text-sm text-slate-600 mt-1">
              Policy changes, market shifts, technology disruption
            </p>
          </div>
        </div>

        {/* Industry Analysis */}
        <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="industry-analysis">
          <div className="flex items-center space-x-3 mb-4">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${selectedScenario === "1.5" ? 'bg-green-100' : 'bg-amber-100'
              }`}>
              <Factory className={`w-5 h-5 ${selectedScenario === "1.5" ? 'text-green-600' : 'text-amber-600'}`} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-[#003B5C]">
                Industry Assessment: {currentOrg?.industry || 'General Business'}
              </h2>
              <p className="text-sm text-slate-500">{scenario.description}</p>
            </div>
          </div>

          <div className="prose prose-slate max-w-none">
            <p className="text-slate-700 leading-relaxed">
              {industryData.summary}
            </p>

            {stateContext && (
              <div className="mt-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                <div className="flex items-center space-x-2 mb-2">
                  <Droplets className="w-4 h-4 text-blue-500" />
                  <span className="font-medium text-slate-700">Regional Context: {currentOrg?.state}</span>
                </div>
                <p className="text-sm text-slate-600">{stateContext}</p>
              </div>
            )}
          </div>
        </div>

        {/* Opportunities & Risks */}
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="opportunities-section">
            <div className="flex items-center space-x-2 mb-4">
              <Leaf className="w-5 h-5 text-green-500" />
              <h3 className="font-semibold text-[#003B5C]">Strategic Opportunities</h3>
            </div>
            <ul className="space-y-3">
              {industryData.opportunities.map((opp, idx) => (
                <li key={idx} className="flex items-start space-x-2">
                  <span className="w-5 h-5 bg-green-100 text-green-600 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                    {idx + 1}
                  </span>
                  <span className="text-sm text-slate-700">{opp}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="risks-section">
            <div className="flex items-center space-x-2 mb-4">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              <h3 className="font-semibold text-[#003B5C]">Key Risks to Monitor</h3>
            </div>
            <ul className="space-y-3">
              {industryData.risks.map((risk, idx) => (
                <li key={idx} className="flex items-start space-x-2">
                  <span className="w-5 h-5 bg-red-100 text-red-600 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                    {idx + 1}
                  </span>
                  <span className="text-sm text-slate-700">{risk}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-sm text-slate-600">
          <strong>Disclaimer:</strong> This stress test provides general guidance based on climate scenarios from the IPCC and NGFS.
          It does not constitute financial advice. Consult a qualified climate risk specialist for detailed scenario analysis
          specific to your operations.
        </div>

        {/* Actions */}
        <div className="flex justify-between items-center">
          <Button
            variant="outline"
            onClick={() => navigate('/risks')}
            className="border-slate-300"
            data-testid="view-risk-register-btn"
          >
            View Climate Risk Register
          </Button>
          <Button
            onClick={() => navigate('/dashboard')}
            className="bg-[#003B5C] hover:bg-[#002A42] text-white"
            data-testid="return-dashboard-btn"
          >
            Return to Dashboard
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ScenarioStressTestPage;
